const identity = (x) => x;
const arr = (typeDef) => (obj) => obj?.map(typeDef);

export const types = {
  String: identity,
  'String!': identity,
  '[String]': arr(identity),
  ID: identity,
  'ID!': identity,
  Boolean: identity,
  Int: identity,
  '[Int]': arr(identity),
  AWSDate: identity,
  AWSDateTime: identity,
  Float: identity,
  'AWSEmail!': identity,
  AWSPhone: identity
};

/**
 * defineType - creates a sanitizer function that receives the object to sanitize
 * and creates a new object containing only the properties in (typeDefs) the field type definition
 * by executing the type accessor function for each type. Storing the sanitizer function in the
 * types dictionary allow you to create composed types.
 *
 * @param {Object} typeDefs field type definitions
 */
const defineType = (typeDefs) => (obj) =>
  obj &&
  Object.entries(typeDefs).reduce((ret, [name, type]) => {
    let value;
    try {
      value = types[type](obj[name]);
    } catch (e) {
      throw Error(`The type [${type}] is failing at field [${name}] with the error:\n${e}`);
    }
    return {
      ...ret,
      [name]: value
    };
  }, {});

types.AddressDetailsInput = defineType({
  address: 'String!',
  address2: 'String',
  country: 'String',
  city: 'String',
  state: 'String',
  zip: 'String'
});

types['AddressDetailsInput!'] = types.AddressDetailsInput;
types['[AddressDetailsInput]'] = arr(types.AddressDetailsInput);

types.AdditionalPhoneNumber = defineType({
  phoneNumber: 'String',
  note: 'String',
  canText: 'Boolean'
});

types.AdditionalPhoneNumbers = arr(types.AdditionalPhoneNumber);

types.AccountDetailsInput = defineType({
  fname: 'String',
  lname: 'String',
  email: 'String',
  id: 'ID',
  phoneNumber: 'String',
  mailingAddress: 'AddressDetailsInput',
  additionalPhoneNumbers: 'AdditionalPhoneNumbers',
  initialPhoneNumber: 'String',
  initialEmail: 'String'
});

types['[AccountDetailsInput]'] = arr(types.AccountDetailsInput);

types.PersonDetailsInput = defineType({
  addDate: 'AWSDate',
  id: 'ID',
  isPrimary: 'Boolean',
  isCoApplicant: 'Boolean',
  firstName: 'String',
  middleName: 'String',
  lastName: 'String',
  suffix: 'String',
  dateOfBirth: 'AWSDate',
  gender: 'String',
  maritalStatus: 'String',
  highestEducation: 'String',
  driversLicenseState: 'String',
  driversLicenseNumber: 'String',
  excludeDriver: 'Boolean',
  ageFirstLicensed: 'Int',
  fullTimeStudent: 'Boolean',
  goodStudent: 'Boolean',
  takenCourse: 'Boolean',
  courseDiscountConditionCheck: 'Boolean',
  schoolName: 'String',
  schoolLocation: 'AddressDetailsInput',
  communityDriveParticipation: 'Boolean',
  waivedPIPWL: 'Boolean',
  pipEligible: 'Boolean',
  pipOptOut: 'Boolean',
  pipHasQHC: 'Boolean',
  assignedCar: 'String',
  userAssignedCar: 'String',
  gotDrivingRecord: 'Boolean',
  clearUDRViolation: 'Boolean',
  needSR22: 'Boolean',
  lapsedLicense: 'Boolean',
  exclusionReason: 'String',
  removedReason: 'String',
  removedBy: 'String',
  removedTimestamp: 'AWSDateTime'
});

types['[PersonDetailsInput]'] = arr(types.PersonDetailsInput);

types.CarDetailsInput = defineType({
  addDate: 'AWSDate',
  VIN: 'String',
  primaryUse: 'String',
  purchaseDate: 'AWSDate',
  garageLocation: 'AddressDetailsInput',
  isGarageAddressRuleExceptionApproved: 'Boolean',
  deductibleCollision: 'String',
  deductibleComprehensive: 'String',
  coverageLoan: 'Boolean',
  limitUMPD: 'String',
  // NM per-vehicle UM/UIM rejection (#29925). Must be in the allowlist or
  // QuoteDetailsInput strips it on Update Offer Price, so the rater never
  // applies the rejection and the form stays permanently dirty (#31403).
  umuimRejected: 'Boolean',
  limitRental: 'String',
  limitACPE: 'String',
  limitIncomeLoss: 'String',
  roadsideAssistance: 'Boolean',
  rideSharing: 'Boolean',
  confirmed: 'Boolean',
  aebStatus: 'String',
  aebConfirmed: 'Boolean',
  antiTheftStatus: 'String',
  antiTheftConfirmed: 'Boolean',
  blindSpotStatus: 'String',
  blindSpotConfirmed: 'Boolean',
  singleAirbagStatus: 'String',
  singleAirbagConfirmed: 'Boolean',
  doubleAirbagStatus: 'String',
  doubleAirbagConfirmed: 'Boolean',
  assignedDriver: 'String',
  userAssignedDriver: 'String',
  luxury: 'Boolean',
  symbolMake: 'String',
  symbolModel: 'String',
  symbolStyle: 'String',
  symbolAux: 'String',
  manuallyAddedSymbols: 'Boolean',
  make: 'String',
  model: 'String',
  year: 'Int',
  annualMilesDriven: 'Int',
  symbolPGS: 'String',
  symbolBI: 'String',
  symbolPD: 'String',
  symbolMed: 'String',
  symbolPIP: 'String',
  symbolColl: 'String',
  symbolComp: 'String',
  symbolUM: 'String'
});

types.TrailerDetailsInput = defineType({
  addDate: 'AWSDate',
  VIN: 'String',
  type: 'String',
  primaryUse: 'String',
  purchaseDate: 'AWSDate',
  garageLocation: 'AddressDetailsInput',
  value: 'Int',
  contents: 'Int',
  year: 'Int',
  deductibleCollision: 'String',
  deductibleComprehensive: 'String',
  coverageLoan: 'Boolean',
  confirmed: 'Boolean'
});

types['[CarDetailsInput]'] = arr(types.CarDetailsInput);
types['[TrailerDetailsInput]'] = arr(types.TrailerDetailsInput);

types.AutoDetailsInput = defineType({
  householdMembers: 'Int',
  hasSetPip: 'Boolean',
  pipResidentsConfirmation: 'Boolean',
  pipEligibilityConfirmation: 'Boolean',
  pipAllResidents: 'Int',
  pipEveryoneOnSamePlan: 'Boolean',
  pipResidentsWithQHC: 'Int',
  pipExcludedResidents: 'Int',
  pipAdditionalResidents: 'Int',
  pipHealthInsuranceType: 'String',
  residenceInsuranceType: 'String'
});

types.AutoCoverageDetailsInput = defineType({
  policyLimitBIPD: 'String',
  policyLimitUMBI: 'String',
  policyLimitUIMBI: 'String',
  policyLimitPIP: 'String',
  policyLimitUMPD: 'String',
  policyLimitMedicalPayments: 'String',
  policyLimitLPD: 'String',
  petInjury: 'Boolean',
  policyLimitPIPME: 'String',
  policyLimitPIPWL: 'String',
  policyLimitPIPACR: 'String',
  policyLimitPPI: 'String',
  policyLimitIncomeLoss: 'String',
  policyLimitAccidentalDeath: 'String',
  policyLimitFuneralBenefits: 'String',
  policyLimitExtraMedBenefits: 'String',
  policyLimitTortOption: 'String',
  policyLimitComboFBP: 'String',
  policyLimitGuestPIP: 'String',
  policyLimitNoFaultPIP: 'String',
  uimAddedOn: 'Boolean'
});

types.MortgageDetailsInput = defineType({
  loanNumber: 'String',
  mortgageHolderAddress: 'AddressDetailsInput',
  mortgageHolderName: 'String',
  primary: 'Boolean',
  mortgageHolderId: 'String',
  mortgageHolderEmail: 'String',
  mortgageHolderFax: 'String',
  isCustom: 'Boolean'
});

types['[MortgageDetailsInput]'] = arr(types.MortgageDetailsInput);

types.FarmDetailsInput = defineType({
  mortgageDetails: '[MortgageDetailsInput]'
});

types.HomeDetailsInput = defineType({
  basementSqFt: 'Int',
  basementType: 'String',
  constructionType: 'String',
  exteriorWallType: 'String',
  garageCarCapacity: 'Int',
  garageType: 'String',
  homeLocation: 'AddressDetailsInput',
  homeQuality: 'String',
  mortgageDetails: '[MortgageDetailsInput]',
  numFamiliesInDwelling: 'Int',
  numFireplaces: 'Int',
  numFullBathrooms: 'Int',
  numHalfBathrooms: 'Int',
  numOccupants: 'Int',
  numStories: 'Float',
  numWoodBurningStoves: 'Int',
  ownershipStatus: 'String',
  purchaseDate: 'AWSDate',
  purchaseAmount: 'Float',
  roofOriginal: 'Boolean',
  roofShape: 'String',
  roofType: 'String',
  roofYear: 'Int',
  roofYearChangedManually: 'Boolean',
  roofDeck: 'String',
  roofCover: 'String',
  roofDeckAttachment: 'String',
  roofToWallConnection: 'String',
  openingProtection: 'String',
  roofSWR: 'String',
  sqFt: 'Int',
  originalSqFt: 'Int',
  swimmingPool: 'String',
  typeOfHome: 'String',
  yearBuilt: 'Int',
  latitude: 'Float',
  longitude: 'Float',
  windstormMitigationLevel: 'Int',
  stormShutters: 'Boolean',
  fireHydrantWithin1000ft: 'Boolean',
  fortifiedHome: 'Int'
});

types.HomeCoverageDetailsInput = defineType({
  coverageA: 'Float',
  coverageIBSC: 'Int',
  coverageBCPctOfA: 'Int',
  coverageBPctOfA: 'Int',
  coverageCPctOfA: 'Int',
  coverageDPctOfA: 'Int',
  coverageX: 'Int',
  coverageY: 'Int',
  coverageWaterBackup: 'Int',
  coverageBuildingMaterialsTheft: 'Boolean',
  coverageCourseOfConstruction: 'Boolean',
  coverageExtendedPremises: 'Boolean',
  coverageFairRental: 'Boolean',
  coveragePetBite: 'Boolean',
  coverageRSExtended: 'Boolean',
  coverageYardAndGarden: 'Boolean',
  coverageSinkhole: 'Boolean',
  coverageOilStorageTank: 'Boolean',
  coverageEBSL: 'Boolean',
  coverageFortifiedRoof: 'Boolean',
  coverageBP: 'Int',
  coverageCA: 'Int',
  coverageDC: 'Boolean',
  coverageDR: 'Boolean',
  coverageF: 'Int',
  coverageG: 'Int',
  coverageJ: 'Int',
  coverageMI: 'Int',
  coverageMS: 'Boolean',
  coverageMSALE: 'Boolean',
  coverageSP: 'Int',
  coverageST: 'Int',
  homeownerProtection: 'Boolean',
  deductibleAllOther: 'Float',
  deductibleHurricane: 'Float',
  deductibleWindHail: 'Float',
  deductibleWaterBackup: 'Float',
  windHailExclusion: 'Boolean',
  interestedInSeparateWindHail: 'Boolean',
  externalPolicyId: 'String'
});

types.FarmCoverageDetailsInput = defineType({
  identityFraudLimit: 'Int',
  extraExpenseLimit: 'Int',
  propertyInTransitLimit: 'Int',
  hasEquipmentBreakdownCoverage: 'Boolean',
  propertyInCareIncreasedLimit: 'Int',
  fungiPropertyLimit: 'Int',
  fungiLiabilityLimit: 'Int',
  pollutantCleanupLimit: 'Int',
  pollutantCleanupDeductible: 'Int',
  policyLevelAdjustmentFactor: 'Int',
  adjustmentRationale: 'String'
});

types.RentersCoverageDetailsInput = defineType({
  rentersLocation: 'AddressDetailsInput',
  coverageCD: 'String',
  coverageX: 'Int',
  coverageY: 'Int',
  deductible: 'Int'
});

types.UmbrellaCoverageDetailsInput = defineType({
  highRisk: 'Boolean',
  numVehiclesTotal: 'Int',
  numLicensedDriversUnder25: 'Int',
  numMotorcyclesScooters: 'Int',
  numATVs: 'Int',
  numRVs: 'Int',
  numPersonalWatercraft: 'Int',
  watercraftHullLengths: '[Int]',
  presenceOfRVs: 'Boolean',
  numRentalProperties: 'Int',
  rentalPropertyAddresses: '[AddressDetailsInput]',
  numOtherProperties: 'Int',
  otherPropertyAddresses: '[AddressDetailsInput]',
  presenceOfBusiness: 'Boolean',
  uninsuredMotoristLimit: 'String',
  limitBIOccurrence: 'String',
  liabilityCoverageLimit: 'Int'
});

types.EmailDetailsInput = defineType({
  label: 'String',
  email: 'AWSEmail!'
});

types['[EmailDetailsInput]'] = arr(types.EmailDetailsInput);

types.PhoneDetailsInput = defineType({
  phoneType: 'String',
  number: 'AWSPhone',
  extension: 'Int'
});

types['[PhoneDetailsInput]'] = arr(types.PhoneDetailsInput);

types.GlobalDetailsInput = defineType({
  autoEffectiveDate: 'AWSDate',
  homeEffectiveDate: 'AWSDate',
  currentlyHomeInsured: 'Boolean',
  manualInventorySubmission: 'Boolean',
  discountInventoryScore: 'Boolean',
  discountDriverScore: 'Boolean',
  discountPaperless: 'Boolean',
  discountBixMutualAffinity: 'Boolean',
  directBillPreferredPaymentMethod: 'String',
  homeownersPaymentType: 'String',
  homeownersPaymentMethod: 'String',
  autoPaymentType: 'String',
  autoPaymentMethod: 'String',
  currentlyAutoInsured: 'Boolean',
  currentAutoCarrier: 'String',
  continuousAutoCoverageStartDate: 'AWSDate',
  mostRecentAutoInsuranceCoverageEndDate: 'AWSDate',
  currentAutoCarrierStartDate: 'AWSDate',
  currentAutoCarrierEndDate: 'AWSDate',
  currentAutoCarrierPolicyNumber: 'String',
  priorUmIndividualBodilyInjury: 'Int',
  priorUmOccurrenceBodilyInjury: 'Int',
  priorIndividualBILimit: 'Int',
  priorOccurrenceBILimit: 'Int',
  autoBillingDayOfMonth: 'Int',
  homeBillingDayOfMonth: 'Int',
  affinity: 'String',
  affinityGroupStem: 'String',
  breakupWithAuto: 'Boolean',
  currentHomeownersCarrier: 'String',
  currentHomeownersCarrierPolicyNumber: 'String',
  breakupWithHomeowners: 'Boolean',
  personalPropertyProtection: 'Boolean',
  emails: '[EmailDetailsInput]',
  phoneNumbers: '[PhoneDetailsInput]',
  mailingAddress: 'AddressDetailsInput',
  homeSecurityPartnerCustomerType: 'String',
  discountInventoryScoreAddedDate: 'AWSDate',
  employeeDiscount: 'Boolean',
  rentersEffectiveDate: 'AWSDate',
  rentersPaymentType: 'String',
  rentersPaymentMethod: 'String',
  rentersBillingDayOfMonth: 'Int',
  currentRentersCarrier: 'String',
  currentRentersCarrierPolicyNumber: 'String',
  breakupWithRenters: 'Boolean',
  condoEffectiveDate: 'AWSDate',
  condoBillingDayOfMonth: 'Int',
  condoPaymentType: 'String',
  condoPaymentMethod: 'String',
  autoMegaDownPay: 'Boolean',
  homeMegaDownPay: 'Boolean',
  rentersMegaDownPay: 'Boolean',
  condoMegaDownPay: 'Boolean',
  fromApi: 'Boolean',
  fromRC1: 'Boolean',
  farmEffectiveDate: 'AWSDate',
  farmPaymentType: 'String',
  farmPaymentMethod: 'String',
  farmPaymentMode: 'String',
  farmBillingDayOfMonth: 'Int',
  farmMegaDownPay: 'Boolean',
  skipUnderwritingUntil: 'AWSDateTime',
  // Farm discounts
  multiPolicyDiscount: 'Boolean',
  legacyDiscount: 'Boolean',
  militaryDiscount: 'Boolean',
  educationDiscount: 'Boolean',
  traditionsDiscount: 'Boolean',
  farmAssociationDiscount: 'Boolean',
  cropInsuranceDiscount: 'Boolean',
  claimFreeDiscount: 'Boolean',
  memberOfCattlemenAssociation: 'Boolean',
  memberOfSoybeanAssociation: 'Boolean'
});

types.CondoDetailsInput = defineType({
  constructionType: 'String',
  condoLocation: 'AddressDetailsInput',
  hasUnitNumber: 'Boolean',
  condoQuality: 'String',
  mortgageDetails: '[MortgageDetailsInput]',
  numFullBathrooms: 'Int',
  numHalfBathrooms: 'Int',
  numOccupants: 'Int',
  ownershipStatus: 'String',
  purchaseDate: 'AWSDate',
  purchaseAmount: 'Float',
  roofShape: 'String',
  sqFt: 'Int',
  typeOfCondo: 'String',
  typeOfEntry: 'String',
  hasBasement: 'Boolean',
  buildingHasMoreThanFiveUnits: 'Boolean',
  yearBuilt: 'Int',
  latitude: 'Float',
  longitude: 'Float',
  fpc: 'String',
  county: 'String',
  fireHydrantWithin1000ft: 'Boolean',
  windstormMitigationLevel: 'Int',
  fpcFromLocation: 'String',
  floor: 'String',
  weeksRentedOut: 'Int',
  numVehicles: 'Int'
});

types.CondoCoverageDetailsInput = defineType({
  coverageC: 'Float',
  coverageA: 'Float',
  coverageBP: 'Int',
  coverageDPctOfC: 'Int',
  coverageEBSL: 'Boolean',
  coverageX: 'Int',
  coverageY: 'Int',
  coverageBCPctOfC: 'Int',
  coverageMSALE: 'Boolean',
  coverageMS: 'Boolean',
  coverageG: 'Int',
  coverageCA: 'Int',
  coverageDR: 'Boolean',
  coverageF: 'Int',
  coverageJ: 'Int',
  coverageMI: 'Int',
  coverageSP: 'Int',
  coverageST: 'Int',
  coveragePetBite: 'Boolean',
  coverageIBSC: 'Int',
  deductibleAllOther: 'Float',
  coverageWaterBackup: 'Int',
  personalPropReimbursement: 'Boolean',
  minimumDeductibleValue: 'Float'
});

types.scheduledPersonalPropertyItemDetailsInput = defineType({
  category: 'String',
  description: 'String',
  value: 'Int',
  breakageOption: 'Boolean',
  exTheftOption: 'Boolean'
});

types.PreBindUWCheckInput = defineType({
  homeVerification: 'HomeVerificationInput',
  autoVerification: 'AutoVerificationInput',
  farmVerification: 'FarmVerificationInput',
  approvalDetails: 'UWApprovalDetailsInput',
  attestationAgent: 'String',
  overridesForUW: '[Int]'
});

types.UWApprovalDetailsInput = defineType({
  sentToUnderwritingAt: 'AWSDateTime',
  sentToUnderwritingBy: 'String',
  underwriterApprovedAt: 'AWSDateTime',
  underwriterApprovedBy: 'String'
});

types.FarmVerificationInput = defineType({
  // Eligibility questions
  unrepairedDamage: 'Boolean',
  unrepairedDamageDetails: 'String',
  priorFireLoss: 'Boolean',
  priorFireLossDetails: 'String',
  priorLiabilityLoss: 'Boolean',
  priorLiabilityLossDetails: 'String',
  chargedWithFelony: 'Boolean',
  chargedWithFelonyDetails: 'String',
  foreclosureBankruptcy: 'Boolean',
  foreclosureBankruptcyDetails: 'String',
  coverageDeclined: 'Boolean',
  coverageDeclinedDetails: 'String',

  // Property risk factor questions
  otherResidences: 'Boolean',
  otherResidencesDetails: 'String',
  dwellingsForSale: 'Boolean',
  dwellingsForSaleDetails: 'String',
  unlistedStructures: 'Boolean',
  unlistedStructuresDetails: 'String',
  recreationalVehicles: 'Boolean',
  recreationalVehiclesDetails: 'String',
  livestockFencing: 'Boolean',
  livestockFencingDetails: 'String',
  livestockEscape: 'Boolean',
  livestockEscapeDetails: 'String',
  solarPanels: 'Boolean',
  solarPanelsDetails: 'String',
  dogsOrExoticPets: 'Boolean',
  dogsOrExoticPetsDetails: 'String',
  trampoline: 'Boolean',
  trampolineDetails: 'String',
  dwellingsUnderRenovation: 'Boolean',
  dwellingsUnderRenovationDetails: 'String',
  farmStructuresRentedToOthers: 'Boolean',
  farmStructuresRentedToOthersDetails: 'String',

  // Operational details questions
  businessConductedOnPremises: 'Boolean',
  businessConductedOnPremisesDetails: 'String',
  dayCareBusiness: 'Boolean',
  dayCareBusinessDetails: 'String',
  anhydrousAmmoniaEquipment: 'Boolean',
  anhydrousAmmoniaEquipmentDetails: 'String',
  processOrManufacture: 'Boolean',
  processOrManufactureDetails: 'String',
  huntingOrFishingFee: 'Boolean',
  huntingOrFishingFeeDetails: 'String',
  customFarming: 'Boolean',
  customFarmingDetails: 'String',

  // Policy restrictions
  restrictions: 'String',

  // Attestation
  signedFarmUWAttestation: 'Boolean'
});

types.AutoVerificationInput = defineType({
  ineligibleForAutoDueToUWSelfReport: 'Boolean',
  signedPropertyUWAttestation: 'Boolean',
  // the nodes below are for FL only
  needSR22: 'Boolean',
  excludeBICoverage: 'Boolean'
});

types.HomeVerificationInput = defineType({
  signedPropertyUWAttestation: 'Boolean',
  ineligibleForHomeDueToUWSelfReport: 'Boolean',
  noAsbestosCheck: 'String',
  homeWiringCheck: 'String',
  homePlumbingCheck: 'String',
  noOpenFoundationCheck: 'String',
  // the below nodes are specific to the FL UW form
  hasTrampolinesOrSkateBoardRampEtc: 'Boolean',
  isMobileOrPrefabricatedEtc: 'Boolean',
  hadSinkholeActivityOrIsAdjacentToSinkholes: 'Boolean',
  isIneligibleDogBreed: 'Boolean',
  hasExoticOrNonDomesticatedEtcAnimals: 'Boolean',
  hasPetBiteHistoryOrViciousBehavior: 'Boolean',
  hasCommercialOccupation: 'Boolean',
  isOwnedByCorpOrPartnershipEtc: 'Boolean',
  hadRentedMultTimesOrFullTimeOneTenant: 'Boolean',
  isInForeclosure: 'Boolean',
  hasMoreThan2Mortgages: 'Boolean',
  runDayCareOutOfMyHome: 'Boolean',
  isPrimaryHome: 'Boolean',
  hadPriorLossIn5Years: 'Boolean',
  isWaterHeaterAgeGreaterThan15Years: 'Boolean',
  isPriorPolicyCancelledOrNonRenewed: 'Boolean',
  hasFlatRoofs: 'Boolean',
  excludeWindCoverage: 'Boolean',
  excludeContentCoverage: 'Boolean',
  includeSinkholeCoverage: 'Boolean',
  hasRecalledConstructionMaterials: 'Boolean',
  hasInsuranceCurrently: 'Boolean'
});

types['[scheduledPersonalPropertyItemDetailsInput]'] = arr(types.scheduledPersonalPropertyItemDetailsInput);
types.scheduledPersonalPropertyDetailsInput = defineType({
  deductible: 'Int',
  items: '[scheduledPersonalPropertyItemDetailsInput]'
});

types.EarthquakeCoverageDetailsInput = defineType({
  policyId: 'String'
});

types.FloodCoverageDetailsInput = defineType({
  policyId: 'String'
});

types.BranchPlusPolicyDetailsInput = defineType({
  policyType: 'String',
  policyCarrier: 'String',
  policyNumber: 'String',
  attestationAgent: 'String'
});

types['[BranchPlusPolicyDetailsInput]'] = arr(types.BranchPlusPolicyDetailsInput);

types.LiabilityExclusionsInput = defineType({
  excludeActivities: 'Boolean',
  excludeLocations: 'Boolean',
  excludedActivitiesDescription: 'String',
  excludedLocationsDescription: 'String',
  excludedEmployeesDescription: 'String'
});

types.EmployeeCounterInput = defineType({
  partTime: 'Int',
  fullTime: 'Int',
  partTime40DaysOrLess: 'Int'
});

types.FarmWorkDetailInput = defineType({
  type: 'String',
  description: 'String',
  amount: 'Float',
  includesSpecificActivities: 'Boolean'
});
types['[FarmWorkDetailInput]'] = arr(types.FarmWorkDetailInput);

types.CustomFeedingDetailInput = defineType({
  id: 'ID',
  type: 'String',
  description: 'String',
  associatedLocations: '[String]'
});
types['[CustomFeedingDetailInput]'] = arr(types.CustomFeedingDetailInput);

types.BusinessActivityInput = defineType({
  type: 'String',
  description: 'String',
  namedInsureds: '[String]',
  includesSpecificActivities: 'Boolean'
});
types['[BusinessActivityInput]'] = arr(types.BusinessActivityInput);

types.BusinessPursuitInput = defineType({
  type: 'Int',
  amount: 'Float',
  namedInsureds: '[String]'
});
types['[BusinessPursuitInput]'] = arr(types.BusinessPursuitInput);

types.OccupancyDetailInput = defineType({
  id: 'ID',
  type: 'String',
  description: 'String',
  location: 'String'
});
types['[OccupancyDetailInput]'] = arr(types.OccupancyDetailInput);

types.EmployeeDetailsInput = defineType({
  id: 'ID',
  firstName: 'String',
  lastName: 'String'
});
types['[EmployeeDetailsInput]'] = arr(types.EmployeeDetailsInput);

types.PropertyLiabilityExtensionInput = defineType({
  id: 'ID',
  type: 'String',
  locationId: 'String',
  riskId: 'String',
  coverage: 'String',
  legalDescription: 'LegalDescriptionInput',
  additionalLegalDescription: 'String',
  locationDirections: 'String',
  addressDetails: 'FarmAddressDetailsInput',
  description: 'String',
  numFamilies: 'Int',
  numberOfAcres: 'Int',
  structureDetail: 'String'
});
types['[PropertyLiabilityExtensionInput]'] = arr(types.PropertyLiabilityExtensionInput);

types.AssetLiabilityExtensionInput = defineType({
  id: 'ID',
  type: 'String',
  locationId: 'String',
  riskId: 'String',
  existingAssetId: 'String',
  name: 'String',
  year: 'Int',
  make: 'String',
  model: 'String',
  serialNumber: 'String',
  limit: 'String',
  description: 'String',
  coverage: 'String',
  weight: 'Int',
  cc: 'Int',
  horsepower: 'Int',
  classification: 'String',
  mph: 'Int',
  length: 'Int'
});
types['[AssetLiabilityExtensionInput]'] = arr(types.AssetLiabilityExtensionInput);

types.FarmLiabilityCoverageDetailsInput = defineType({
  type: 'String',
  coverageL: 'Float',
  coverageM: 'Float',
  annualAggregateLimit: 'Float',
  farmOperations: 'String',
  personalInjury: 'Boolean',
  numManMonths: 'Int',
  careProvidedToOthers: 'Int',
  careProvidedToOthersDescription: 'String',
  recreationalVehicleUMUIM: 'String',
  watercraftUMUIM: 'String',
  farmPollutionLimit: 'Int',
  hasExclusions: 'Boolean',
  exclusions: 'LiabilityExclusionsInput',
  employees: 'EmployeeCounterInput',
  additionalPropertiesExtensions: '[PropertyLiabilityExtensionInput]',
  additionalAssetsExtensions: '[AssetLiabilityExtensionInput]',
  domesticEmployees: '[EmployeeDetailsInput]',
  customFarmWorkItems: '[FarmWorkDetailInput]',
  customFeeding: '[CustomFeedingDetailInput]',
  businessActivities: '[BusinessActivityInput]',
  businessPursuits: '[BusinessPursuitInput]',
  occupancies: '[OccupancyDetailInput]'
});

types.PolicyDetailsInput = defineType({
  correctedAddress: 'AddressDetailsInput',
  drivers: '[PersonDetailsInput]',
  nonDrivers: '[PersonDetailsInput]',
  removedDrivers: '[PersonDetailsInput]',
  people: '[PersonDetailsInput]',
  entity: 'EntityDetailsInput',
  cars: '[CarDetailsInput]',
  trailers: '[TrailerDetailsInput]',
  auto: 'AutoDetailsInput',
  autoCoverage: 'AutoCoverageDetailsInput',
  home: 'HomeDetailsInput',
  attachedHomeowners: 'Boolean',
  attachedRenters: 'Boolean',
  attachedAuto: 'Boolean',
  attachedCondo: 'Boolean',
  homeCoverage: 'HomeCoverageDetailsInput',
  connectedHome: 'ConnectedHomeDetailsInput',
  includeUmbrella: 'Boolean',
  includeRenters: 'Boolean',
  includeEarthquake: 'Boolean',
  includeFlood: 'Boolean',
  earthquakeCoverage: 'EarthquakeCoverageDetailsInput',
  floodCoverage: 'FloodCoverageDetailsInput',
  rentersCoverage: 'RentersCoverageDetailsInput',
  umbrellaCoverage: 'UmbrellaCoverageDetailsInput',
  scheduledPersonalProperty: 'scheduledPersonalPropertyDetailsInput',
  global: 'GlobalDetailsInput',
  startDate: 'AWSDate',
  multiPolicyDiscount: 'String',
  underwritingChanges: 'Boolean',
  skipImmediateBillOrRefund: 'Boolean',
  applyFee: 'Boolean',
  condo: 'CondoDetailsInput',
  condoCoverage: 'CondoCoverageDetailsInput',
  branchPlusPolicies: '[BranchPlusPolicyDetailsInput]',
  farm: 'FarmDetailsInput',
  farmLocations: '[LocationDetailInput]',
  farmCoverage: 'FarmCoverageDetailsInput',
  includeFarmLiability: 'Boolean',
  farmLiabilityCoverage: 'FarmLiabilityCoverageDetailsInput'
  // farmInterestedParties intentionally omitted: it is a policy-level field (PolicyInput /
  // QuoteDetailsInput), not a segment field. #31942 removed it from the schema's
  // PolicyDetailsInput, so including it here would make changeSegment/addSegment send an
  // input field AppSync rejects. The backend preserves policy-level parties. Issue #31945.
});

types.AssociatedEntityInput = defineType({
  entityType: 'String',
  locationId: 'ID',
  riskId: 'ID',
  itemId: 'ID'
});

types['[AssociatedEntityInput]'] = arr(types.AssociatedEntityInput);

types.FO41DetailsInput = defineType({
  description: 'String',
  locations: '[String]',
  risks: '[String]',
  equipment: '[String]',
  sppItems: '[String]',
  rpsItems: '[String]'
});

types.FarmAdditionalPartyDetailsInput = defineType({
  id: 'ID',
  name: 'String',
  email: 'String',
  address: 'AddressDetailsInput',
  relationship: 'String',
  description: 'String',
  isResident: 'Boolean',
  entityType: 'String',
  fo41: 'FO41DetailsInput',
  associatedEntities: '[AssociatedEntityInput]'
});

types['[FarmAdditionalPartyDetailsInput]'] = arr(types.FarmAdditionalPartyDetailsInput);

types.AdditionalPartyDetailsInput = defineType({
  id: 'ID',
  name: 'String',
  email: 'String',
  address: 'AddressDetailsInput',
  lossPayee: 'Boolean',
  relationship: 'String',
  loanNumber: 'String',
  VIN: 'String',
  addressActiveStatus: 'Boolean',
  associatedEntities: '[String]'
});

types['[AdditionalPartyDetailsInput]'] = arr(types.AdditionalPartyDetailsInput);

types.EntityDetailsInput = defineType({
  id: 'ID',
  type: 'String',
  name: 'String',
  address: 'AddressDetailsInput'
});

types['[EntityDetailsInput]'] = arr(types.EntityDetailsInput);

types.BankAchAccountDetailsInput = defineType({
  id: 'String',
  basisTheoryBankAccountToken: 'String'
});

types['[BankAchAccountDetailsInput]'] = arr(types.BankAchAccountDetailsInput);

types.PaymentMethodInput = defineType({
  id: 'String',
  basisTheoryCardToken: 'String'
});

types['[PaymentMethodInput]'] = arr(types.PaymentMethodInput);

types.FeeDetailsInput = defineType({
  type: 'String',
  description: 'String',
  amount: 'Float',
  appliedDate: 'String'
});

types['[FeeDetailsInput]'] = arr(types.FeeDetailsInput);

types.PolicyInput = defineType({
  id: 'String',
  billingAddInstallments: 'Int',
  billingDayOfMonth: 'Int',
  billingHoldUntil: 'AWSDate',
  blockCreditCheckAtRenewal: 'Boolean',
  effectiveDate: 'AWSDate',
  endDate: 'AWSDate',
  transactionDate: 'AWSDate',
  autoRenew: 'Boolean',
  renewalPaymentType: 'String',
  renewalPaymentMethod: 'String',
  renewalCreditPull: 'Boolean',
  rewriteReason: 'String',
  cancelReason: 'String',
  nonRenewReason: 'String',
  additionalParties: '[AdditionalPartyDetailsInput]',
  farmInterestedParties: '[FarmAdditionalPartyDetailsInput]',
  fees: '[FeeDetailsInput]',
  skipImmediateBillOrRefund: 'Boolean',
  applyFee: 'Boolean',
  sendPaperDocuments: 'Boolean',
  paymentMethod: 'String',
  paymentType: 'String',
  defaultBankAccount: 'BankAchAccountDetailsInput',
  defaultCreditCard: 'PaymentMethodInput',
  defaultEscrowAccount: 'MortgageDetailsInput',
  stripeCustomerId: 'String',
  nonRenewed: 'Boolean',
  fullTermCancel: 'Boolean',
  isAgentSold: 'Boolean',
  version: 'Int',
  homeInspectionRepairsRequested: 'Boolean'
});

types.AccountInput = defineType({
  id: 'String',
  fname: 'String',
  lname: 'String',
  mailingAddress: 'AddressDetailsInput',
  phoneNumber: 'AWSPhone',
  email: 'AWSEmail',
  initialEmail: 'String',
  initialPhoneNumber: 'String'
});

types.ConnectedHomeDetailsInput = defineType({
  monitored: 'String',
  moistureDevices: 'Int',
  theftPreventionDevices: 'Int',
  theftPreventionDeviceType: 'String',
  motionDetectingDevices: 'Int',
  smokeDetectingDevices: 'Int',
  fireProtectiveDeviceType: 'String',
  internetMonitoring: 'Boolean',
  autoWaterShutoffDevices: 'Boolean',
  providerName: 'String',
  agreeToTerms: 'Boolean'
});

types.ExistingCustomerInput = defineType({
  id: 'String'
});

types.OverridesForUWInput = defineType({
  code: 'Int',
  overridingAgent: 'String',
  type: 'String'
});

types.OverridesInput = defineType({
  overridesForUW: '[OverridesForUWInput]'
});
types['[OverridesForUWInput]'] = arr(types.OverridesForUWInput);

types.LegalDescriptionInput = defineType({
  format: 'String!',
  section: 'String',
  township: 'String',
  townshipCoordinates: 'String',
  range: 'String',
  rangeCoordinates: 'String',
  lot: 'String',
  block: 'String'
});

types['LegalDescriptionInput!'] = types.LegalDescriptionInput;

types.SwimmingPoolInput = defineType({
  typeOfPool: 'String',
  hasRemovableStepsOrLockingGate: 'Boolean',
  has4FtHighFenceWithSelfLatchingGate: 'Boolean',
  hasDivingBoardOrPoolSlide: 'Boolean',
  limit: 'Int'
});

types.FarmDwellingRenovationInput = defineType({
  electricalType: 'String',
  electricalService: 'String',
  electricalYear: 'Int',
  heatingType: 'String',
  heatingYear: 'Int',
  coolingYear: 'Int',
  plumbingYear: 'Int'
});

types.FarmDwellingDetailsInput = defineType({
  propertyType: 'String',
  basementSqFt: 'Int',
  basementType: 'String',
  constructionType: 'String',
  exteriorWallType: 'String',
  garageCarCapacity: 'Int',
  garageType: 'String',
  dwellingLocation: 'AddressDetailsInput',
  homeQuality: 'String',
  mortgageDetails: '[MortgageDetailsInput]',
  numFamiliesInDwelling: 'Int',
  numFireplaces: 'Int',
  numFullBathrooms: 'Int',
  numHalfBathrooms: 'Int',
  numOccupants: 'Int',
  numStories: 'Float',
  numWoodBurningStoves: 'Int',
  ownershipStatus: 'String',
  purchaseDate: 'AWSDate',
  purchaseAmount: 'Float',
  residenceUsage: 'String',
  residenceType: 'String', // For dwelling contents (rental, seasonal, vacant) - FO6.jsx:612
  roofOriginal: 'Boolean',
  roofShape: 'String',
  roofDeck: 'String',
  roofCover: 'String',
  roofDeckAttachment: 'String',
  roofToWallConnection: 'String',
  openingProtection: 'String',
  roofSWR: 'String',
  roofType: 'String',
  roofYear: 'Int',
  roofYearChangedManually: 'Boolean',
  sqFt: 'Int',
  originalSqFt: 'Int',
  swimmingPool: 'SwimmingPoolInput',
  typeOfHome: 'String',
  yearBuilt: 'Int',
  latitude: 'Float',
  longitude: 'Float',
  stormShutters: 'Boolean',
  porchSqFt: 'Int',
  porchCode: 'String',
  constructionStyle: 'String',
  areasUnderConstruction: 'Boolean',
  hasTrampoline: 'Boolean',
  numWellPumps: 'Int',
  wellPumpEstimatedValue: 'Int',
  wellPumpDescription: 'String',
  numSolidFuelBurningAppliances: 'Int',
  primaryHeatingSource: 'String',
  secondaryHeatingSource: 'String',
  containsAboveGroundPool: 'Boolean',
  mobileHomeConfig: 'String',
  mobileHomeQualityGrade: 'String',
  renovation: 'FarmDwellingRenovationInput',
  // Barn/Outbuilding structure fields (from FarmStructureDetailsInput)
  buildingType: 'String',
  buildingClass: 'Int',
  structureOpenSides: 'Boolean',
  occupancy: 'String',
  solarMountDwelling: 'Boolean',
  solarMountOutbuilding: 'Boolean',
  solarMountGround: 'Boolean',
  numSolarPanels: 'Int',
  bushelCapacity: 'Int',
  numHeatingSources: 'Int',
  numSecondaryHeatingSources: 'Int',
  buildingQuality: 'String',
  diameter: 'Float',
  height: 'Float',
  length: 'Int',
  width: 'Int',
  attachedStructureSqFt: 'Int',
  enclosedMasonryFoundation: 'Boolean',
  fullSkirtingAroundBase: 'Boolean',
  tieDownType: 'String'
});

types.FarmScheduledPropertyItemDetailsExtendedDetailsInput = defineType({
  year: 'Int',
  horsepower: 'Int',
  serialNumber: 'String',
  classification: 'String',
  dimensions: 'String',
  kindOfAnimal: 'String',
  sex: 'String',
  breed: 'String',
  age: 'Int',
  originalCost: 'Int',
  coinsurance: 'Int',
  days: 'Int',
  startDate: 'AWSDate',
  endDate: 'AWSDate',
  name: 'String',
  description: 'String',
  coverageMS: 'Boolean',
  hasActiveCoverageMS: 'Boolean',
  deductibleAllOther: 'Int',
  deductibleWindHail: 'Int',
  make: 'String',
  model: 'String',
  cc: 'Int',
  weight: 'Int',
  lengthFt: 'Int',
  milesPerHour: 'Int'
});

types.FarmScheduledItemInput = defineType({
  id: 'String',
  name: 'String',
  category: 'String',
  limit: 'Int',
  description: 'String',
  deductible: 'Int',
  extendedDetails: 'FarmScheduledPropertyItemDetailsExtendedDetailsInput',
  linkedItemId: 'String'
});
types['[FarmScheduledItemInput]'] = arr(types.FarmScheduledItemInput);

types.FarmScheduledPersonalPropertyDetailsInput = defineType({
  coverageType: 'String',
  items: '[FarmScheduledItemInput]'
});

types.FarmScheduledPropertyDetailsInput = defineType({
  id: 'ID',
  category: 'String',
  limit: 'Int',
  description: 'String',
  deductible: 'Int',
  deductibleWindHail: 'Float',
  additionalPerils: 'Int',
  multipleLossCoverage: 'Boolean',
  livestockCategory: 'String',
  hasOpenPerils: 'Boolean',
  hasReplacementCostCoverage: 'Boolean',
  serialNumber: 'String',
  make: 'String',
  year: 'Int',
  increaseStackLimit: 'Int',
  quantity: 'Int',
  hasWinterPerils: 'Boolean',
  hasSpecialCattleEndorsement: 'Boolean',
  hasSuffocationOfLivestock: 'Boolean'
});

types['[FarmScheduledPersonalPropertyDetailsInput]'] = arr(types.FarmScheduledPersonalPropertyDetailsInput);
types['[FarmScheduledPropertyDetailsInput]'] = arr(types.FarmScheduledPropertyDetailsInput);

types.DwellingRiskCoverageDetailsInput = defineType({
  coverageA: 'Float',
  coverageB: 'Float',
  coverageC: 'Float',
  coverageD: 'Float',
  coverageE: 'Float',
  coverageF: '[FarmScheduledPropertyDetailsInput]',
  coverageG: 'Float',
  deductible: 'Float',
  deductibleEarthquake: 'Float',
  deductibleEarthquake2: 'Float',
  roofLossSettlement: 'String',
  specialFormCoverage: 'Boolean',
  buildingMaterialTheft: 'Boolean',
  roofExclusion: 'String',
  roofExclusionDetails: 'String',
  hasRoofExclusionRestriction: 'Boolean',
  roofExclusionRestriction: 'String',
  roofExclusionDescription: 'String',
  replacementCostExp: 'Boolean',
  exteriorWallSurfacingExclusion: 'Boolean',
  exteriorWallSurfacingExclusionDetails: 'String',
  otherStructuresExclusion: 'Boolean',
  otherStructuresExclusionDetails: 'String',
  repairRebuildRequirement: 'Boolean',
  expandedOrdinanceOrLaw: 'Boolean',
  hasReplacementCostCoverage: 'Boolean',
  hasEarthquakeExteriorVeneerCoverage: 'Boolean',
  hasSnowWeightCoverage: 'Boolean',
  windHailExclusion: 'Boolean',
  windHailExclusionDetails: 'String',
  otherStructuresRoofExclusion: 'Boolean',
  sidingExclusion: 'Boolean',
  sidingExclusionDetails: 'String',
  scheduleCoverages: '[FarmScheduledPersonalPropertyDetailsInput]',
  coverageWaterBackup: 'Int',
  coverageISBC: 'Int',
  coverageRSExtended: 'Boolean',
  poolLimit: 'Float',
  resUnderConstructionLimit: 'Int',
  resUnderConstructionEffectiveDate: 'AWSDate',
  inlandFloodInceptionDate: 'String',
  inlandFloodOccurrenceLimit: 'Int',
  inlandFloodAggregateLimit: 'Int',
  inlandFloodDeductible: 'Int',
  inlandFloodDeductibleOverride: 'Int',
  inlandFloodRiskScore: 'Int',
  serviceLine: 'Boolean',
  personalPropertyReplCost: 'Boolean',
  replCostMobileHome: 'Boolean',
  businessPropertyLimit: 'Int',
  businessPropertyLimitDetails: 'String',
  moneyLimit: 'Int',
  jewelryLimit: 'Int',
  securitiesLimit: 'Int',
  motorizedVehiclesLimit: 'Int',
  silverwareLimit: 'Int',
  gunsLimit: 'Int',
  exteriorDoorsWindowsDamageExclusion: 'Boolean',
  exteriorWallsDamageExclusion: 'Boolean',
  roofsDamageExclusion: 'Boolean',
  cosmeticDamageExclusion: 'Boolean',
  lossSettlement: 'String',
  structureLossSettlement: 'String', // Primary field mentioned by user (FO6.jsx:998)
  contentsLimit: 'Float',
  deductibleAllOther: 'String', // Deductible field (FO6.jsx:967) - corrected naming - String for farm insurance
  deductibleWindHail: 'String', // Deductible field (FO6.jsx:977) - corrected naming
  canEditCoverageAmount: 'Boolean',
  acvFloorApprovedByUW: 'Boolean', // #31836: UW-approved sub-floor Coverage E marker
  adjustDwellingLimitDescription: 'String',
  sprinklerLeakageBuildingLimit: 'Int',
  sprinklerLeakageLimitDetails: 'String',
  coverageMS: 'Boolean',
  hasActiveCoverageMS: 'Boolean'
});

types.FarmRentersDetailsInput = defineType({
  numFamiliesInDwelling: 'Int',
  numSolidFuelBurningAppliances: 'Int',
  constructionType: 'String',
  dwellingLocation: 'AddressDetailsInput'
});

types.FarmRentersRiskCoverageDetailsInput = defineType({
  coverageC: 'Float',
  coverageD: 'Float',
  deductibleAllOther: 'String',
  deductibleWindHail: 'String',
  deductibleEarthquake: 'Float',
  coverageWaterBackup: 'Int',
  poolLimit: 'Float',
  inlandFloodInceptionDate: 'String',
  inlandFloodOccurrenceLimit: 'Int',
  inlandFloodDeductible: 'Int',
  inlandFloodDeductibleOverride: 'Int',
  tenantImprovementLimit: 'Int',
  businessPropertyLimit: 'Int',
  businessPropertyLimitDetails: 'String',
  personalPropertyReplCost: 'Boolean',
  replCostMobileHome: 'Boolean',
  expandedDebrisRemoval: 'Boolean',
  scheduleCoverages: '[FarmScheduledPersonalPropertyDetailsInput]',
  moneyLimit: 'Int',
  jewelryLimit: 'Int',
  securitiesLimit: 'Int',
  motorizedVehiclesLimit: 'Int',
  silverwareLimit: 'Int',
  gunsLimit: 'Int'
});

types.FarmRiskDetailInput = defineType({
  id: 'ID',
  type: 'String!',
  description: 'String!',
  adjustmentFactor: 'Int',
  building: 'FarmDwellingDetailsInput',
  coverages: 'DwellingRiskCoverageDetailsInput',
  connectedHome: 'ConnectedHomeDetailsInput'
});
types['[FarmRiskDetailInput]'] = arr(types.FarmRiskDetailInput);

types.FarmAddressDetailsInput = defineType({
  address: 'String',
  address2: 'String',
  unit: 'String',
  country: 'String',
  county: 'String',
  city: 'String',
  state: 'String',
  zip: 'String',
  section: 'String',
  township: 'String',
  townshipCoordinates: 'String',
  range: 'String',
  rangeCoordinates: 'String',
  lot: 'String',
  block: 'String'
});
types['FarmAddressDetailsInput!'] = types.FarmAddressDetailsInput;

types.LocationDetailInput = defineType({
  id: 'ID',
  description: 'String!',
  legalDescription: 'LegalDescriptionInput',
  additionalLegalDescription: 'String',
  locationDirections: 'String',
  numberOfAcres: 'Int',
  address: 'FarmAddressDetailsInput!',
  fpc: 'String',
  fpcFromLocation: 'String',
  fireHydrantWithin1000ft: 'Boolean',
  risks: '[FarmRiskDetailInput]',
  latitude: 'String',
  longitude: 'String'
});
types['[LocationDetailInput]'] = arr(types.LocationDetailInput);

types.LocationDetailsInput = defineType({
  address: 'AddressDetailsInput!',
  description: 'String!',
  acres: 'Int!',
  legalDescription: 'LegalDescriptionInput!'
});

// Keep backward compatibility by aliasing the old name to the new one
types.FarmLocationDetailsInput = types.LocationDetailInput;

types['[FarmLocationDetailsInput]'] = arr(types.FarmLocationDetailsInput);

types.QuoteDetailsInput = defineType({
  fname: 'String',
  lname: 'String',
  drivers: '[PersonDetailsInput]',
  removedDrivers: '[PersonDetailsInput]',
  nonDrivers: '[PersonDetailsInput]',
  cars: '[CarDetailsInput]',
  trailers: '[TrailerDetailsInput]',
  auto: 'AutoDetailsInput',
  autoCoverage: 'AutoCoverageDetailsInput',
  existingCustomer: 'ExistingCustomerInput',
  home: 'HomeDetailsInput',
  condo: 'CondoDetailsInput',
  condoCoverage: 'CondoCoverageDetailsInput',
  correctedAddress: 'AddressDetailsInput',
  homeCoverage: 'HomeCoverageDetailsInput',
  farmLocations: '[LocationDetailInput]',
  farm: 'FarmDetailsInput',
  farmCoverage: 'FarmCoverageDetailsInput',
  includeFarmLiability: 'Boolean',
  farmLiabilityCoverage: 'FarmLiabilityCoverageDetailsInput',
  includeUmbrella: 'Boolean',
  includeRenters: 'Boolean',
  includeEarthquake: 'Boolean',
  includeFlood: 'Boolean',
  earthquakeCoverage: 'EarthquakeCoverageDetailsInput',
  floodCoverage: 'FloodCoverageDetailsInput',
  isHomeOwner: 'Boolean',
  rentersCoverage: 'RentersCoverageDetailsInput',
  scheduledPersonalProperty: 'scheduledPersonalPropertyDetailsInput',
  umbrellaCoverage: 'UmbrellaCoverageDetailsInput',
  global: 'GlobalDetailsInput',
  leadSource: 'String',
  selectedOption: 'String',
  connectedHome: 'ConnectedHomeDetailsInput',
  noBindHome: 'Boolean',
  noBindAuto: 'Boolean',
  fromStaff: 'Boolean',
  rep: 'String',
  preBindUWCheck: 'PreBindUWCheckInput',
  autoMegaDownPay: 'Boolean',
  homeMegaDownPay: 'Boolean',
  rentersMegaDownPay: 'Boolean',
  condoMegaDownPay: 'Boolean',
  farmMegaDownPay: 'Boolean',
  overrides: 'OverridesInput',
  getMVRs: 'Boolean',
  branchPlusPolicies: '[BranchPlusPolicyDetailsInput]',
  forcedMultiPolicyDiscount: 'String',
  attachedHomeowners: 'Boolean',
  attachedRenters: 'Boolean',
  attachedAuto: 'Boolean',
  attachedCondo: 'Boolean',
  interestedParties: '[AdditionalPartyDetailsInput]',
  lienHolders: '[AdditionalPartyDetailsInput]',
  farmInterestedParties: '[FarmAdditionalPartyDetailsInput]',
  entity: 'EntityDetailsInput'
});
